import { makeObservable, observable, action } from 'mobx';
import { trackEvent } from '@/lib/telemetry';
import type { RootStore } from './RootStore';

export type ThemeMode = 'light' | 'dark';
export type Density = 'compact' | 'comfortable';

const STORAGE_KEY_MODE = 'agent_studio_theme_mode';
const STORAGE_KEY_DENSITY = 'agent_studio_theme_density';

// Persisted UI preferences. Lives in localStorage (NOT sessionStorage) because
// users want their light/dark choice to survive a closed tab.

export class ThemeStore {
  @observable mode: ThemeMode = 'light';
  @observable density: Density = 'comfortable';

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
    this.hydrate();
    this.applySystemPreferenceIfUnset();
  }

  private hydrate(): void {
    try {
      const m = window.localStorage.getItem(STORAGE_KEY_MODE);
      if (m === 'light' || m === 'dark') this.mode = m;
      const d = window.localStorage.getItem(STORAGE_KEY_DENSITY);
      if (d === 'compact' || d === 'comfortable') this.density = d;
    } catch {
      // localStorage may throw in incognito with strict settings — ignore.
    }
  }

  private applySystemPreferenceIfUnset(): void {
    // If the user never explicitly chose, follow system dark-mode setting on first paint.
    try {
      if (window.localStorage.getItem(STORAGE_KEY_MODE) == null) {
        const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
        if (systemDark) this.mode = 'dark';
      }
    } catch {
      // ignore
    }
  }

  @action
  setMode(mode: ThemeMode): void {
    this.mode = mode;
    try {
      window.localStorage.setItem(STORAGE_KEY_MODE, mode);
    } catch {
      // ignore
    }
    trackEvent('Theme.ModeChanged', { mode });
  }

  @action
  toggleMode(): void {
    this.setMode(this.mode === 'light' ? 'dark' : 'light');
  }

  @action
  setDensity(density: Density): void {
    this.density = density;
    try {
      window.localStorage.setItem(STORAGE_KEY_DENSITY, density);
    } catch {
      // ignore
    }
    trackEvent('Theme.DensityChanged', { density });
  }
}
